/**
 * Typed API client for the Building Domain.
 * All Building Domain calls go through this module — never raw fetch in components.
 *
 * Types here must stay in sync with the backend OpenAPI schema.
 * Run `npm run generate:types` to regenerate `generated-building.ts` from the live schema,
 * then reconcile any drift in the hand-written types below.
 */
import type { InventorySearch } from "@/lib/inventory-scope-codec";
import type { RoleId } from "@/lib/roles";
import type { ZoneKpi } from "@/lib/api/agent";

const BASE = "/api/building";
// Logins, token refresh and the signed-in user are the identity service's (ADR-031). They stay on
// this client because `request` — the one 401 seam every domain client shares — refreshes through
// them.
const IDENTITY_BASE = "/api/identity";

export type BuildingType =
  "Toimisto" | "Liiketila" | "Logistiikka" | "Kampus" | "Asuin";
export type BuildingStatus = "ok" | "warn" | "alert";
export type RecipeStatus =
  "pending" | "simulated" | "scheduled" | "applied" | "superseded";
export type OptimizationMechanism =
  | "electricity_price"
  | "weather_forecast"
  | "demand_response"
  | "building_usage"
  | "building_dynamics";

export interface Building {
  id: string;
  name: string;
  address: string;
  city: string;
  building_type: BuildingType;
  status: BuildingStatus;
  optimization_potential_eur_month: number | null;
  active_optimizations: number;
  live_optimization_enabled: boolean;
  locations: string[];
  // Nullable/optional until the backend resolves them (explicit input or geocode-on-write,
  // #1360); never a fabricated/city-centroid default — see ADR-018. Matches
  // generated-building.ts's `Building.lat`/`lon` (`?: number | null`).
  lat?: number | null;
  lon?: number | null;
  created_at: string;
  updated_at: string;
}

export interface BuildingSummary {
  building_id: string;
  total_energy_kwh: number;
  average_occupancy: number;
  active_recipes: number;
  zone_count: number;
}

export interface HourlySample {
  hour: string; // ISO datetime
  energy_kwh: number;
  temperature: number;
  occupancy: number;
  ventilation_rate: number;
}

export interface BuildingAnalytics {
  building_id: string;
  from_ts: string;
  to_ts: string;
  snapshot_count: number;
  total_energy_kwh: number;
  average_temperature: number;
  average_occupancy: number;
  average_ventilation_rate: number;
  hourly_series: HourlySample[];
}

export interface Zone {
  id: string;
  building_id: string;
  name: string;
  floor: number;
  icon: string;
  zone_type: string | null;
  description: string;
  updated_at: string;
}

export interface Equipment {
  id: string;
  building_id: string;
  name: string;
  equipment_type: string;
  zone_id: string | null;
  zone_ids: string[];
  location: string | null;
  equipment_tag: string;
  state: Record<string, unknown>;
  // Whether the optimization loop may generate recipes for this equipment — the
  // per-equipment opt-in, mirroring Building.live_optimization_enabled's coarser gate.
  optimization_enabled: boolean;
  declared_variants: string[];
  declared_data_points: string[];
  // Per-instance process-path order of the active components (#682) — falls back to the
  // KB type's catalog order when empty. Never gates presence — that stays declared_variants.
  declared_component_order: string[];
  // Per-instance physical sub-type choice per component (#682), e.g.
  // {"heat-recovery-unit": "rotary"}.
  declared_component_subtypes: Record<string, string>;
  // Per-instance, per-stream process-path order (#1153/#1156) — {stream_id: component_id[]}.
  // A stream absent here falls back to the KB type's own streams.yaml order for that stream.
  declared_stream_component_order: Record<string, string[]>;
  updated_at: string;
}

export interface ZoneExpectedBehaviorItem {
  text_fi: string;
  data_requirements: string[];
  from_template_version: number | null;
  is_active: boolean;
  sort_order: number;
}

export interface ZoneExpectedBehaviors {
  zone_id: string;
  items: ZoneExpectedBehaviorItem[];
}

export interface RecipeAction {
  parameter: string;
  baseline: number;
  offset: number;
  reason: string;
  bms_instruction: string | null;
  unit: string | null;
  scheduled_at: string | null; // ISO datetime
  mechanism: OptimizationMechanism | null;
}

export interface ImpactEstimate {
  energy_delta_kwh: number;
  comfort_score_delta: number;
  estimated_savings_eur_month: number;
  confidence: number;
  estimated_at: string;
}

export interface MechanismContribution {
  mechanism: OptimizationMechanism;
  energy_delta_kwh: number;
  savings_eur_month: number;
  action_count: number;
}

export interface EquipmentContribution {
  equipment_id: string | null;
  equipment_name: string;
  energy_delta_kwh: number;
  savings_eur_month: number;
  action_count: number;
}

export interface DailyComparison {
  date: string; // "YYYY-MM-DD"
  actual_energy_kwh: number;
  optimised_energy_kwh: number;
}

export interface SimulationResult {
  total_energy_delta_kwh: number;
  total_savings_eur_month: number;
  confidence: number;
  mechanism_contributions: MechanismContribution[];
  equipment_contributions: EquipmentContribution[];
  simulated_at: string;
  daily_comparisons: DailyComparison[];
}

/** One signal a recipe moved, measured before and after it applied (ADR-033). */
export interface SignalResponse {
  data_point_id: string;
  unit: string;
  before_mean: number;
  after_mean: number;
  delta: number;
  samples_before: number;
  samples_after: number;
}

/**
 * What the signals a recipe moved actually did. A measurement, unlike `actual_impact`, which is
 * the forward estimate recomputed with hindsight data.
 *
 * `not_measured` names the moved signals that had readings on only one side of the change. They
 * are shown as gaps, never as a zero delta — a zero would say the signal did not move.
 */
export interface MeasuredResponse {
  measured_at: string;
  window_hours: number;
  signals: SignalResponse[];
  not_measured: string[];
}

export interface Recipe {
  id: string;
  source_agent_id: string;
  building_id: string;
  // The one equipment item this recipe's actions target — a recipe is scoped to exactly
  // one equipment item, not a mixed bag of actions across arbitrary equipment.
  equipment_id: string;
  zone_id: string | null;
  actions: RecipeAction[];
  reasoning_summary: string;
  status: RecipeStatus;
  scheduled_for: string | null;
  simulated_impact: ImpactEstimate | null;
  /** The forward estimate recomputed with hindsight data — not a measurement (ADR-033). */
  actual_impact: ImpactEstimate | null;
  measured_response: MeasuredResponse | null;
  mechanisms: OptimizationMechanism[];
  simulation_result: SimulationResult | null;
  created_by: string;
  created_at: string;
  applied_at: string | null;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface UserMe {
  id: string;
  email: string;
  rbac_role: RoleId;
}

// Called by auth.ts on module init so the in-memory session is cleared alongside sessionStorage.
let _onUnauthorized: (() => void) | null = null;
export function registerUnauthorizedHandler(handler: () => void): void {
  _onUnauthorized = handler;
}

let _redirectingToLogin = false;

// The single guarded 401 handler shared by every API client. Clears the in-memory session (via the
// registered handler) and the stored token, then redirects to /login exactly once — never while
// already on /login, and never twice. Exported so the SSE stream (which streams and so cannot route
// through `request`) uses the same path.
export function handleUnauthorized(): void {
  if (_redirectingToLogin || window.location.pathname === "/login") return;
  _redirectingToLogin = true;
  // Drop the in-memory token and let the registered handler (auth.ts) clear the session, then
  // redirect once. The api layer keeps a single token seam only.
  setAccessToken(null);
  _onUnauthorized?.();
  window.location.href = "/login";
}

// Carries the HTTP status so callers can distinguish, e.g., a 404 (resource genuinely absent — a
// valid state to render) from a transient 5xx/network failure. `message` matches the previous plain
// Error, so existing `catch (e) => e.message` callers keep working.
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// The access token is held in memory only — never sessionStorage/localStorage — so injected
// scripts can't read it and it dies with the tab. Persistence across a hard reload comes from the
// httpOnly refresh cookie + silent refresh (story #208), not from web storage.
let _accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

// The single token-read site for the whole api layer. Every client's Authorization header comes
// from here, and it now reads the in-memory token rather than sessionStorage.
export function authHeaders(): HeadersInit {
  return _accessToken ? { Authorization: `Bearer ${_accessToken}` } : {};
}

// The single request implementation for every domain client. `base` defaults to the building
// domain; agent.ts/device.ts pass their own base so all three share this one guarded 401 path,
// one `authHeaders` seam, and one `ApiError` shape.
export async function request<T>(
  path: string,
  init: RequestInit = {},
  base: string = BASE,
): Promise<T> {
  // credentials:"include" sends the httpOnly refresh cookie (same-origin behind nginx).
  const send = () =>
    fetch(`${base}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
        ...init.headers,
      },
    });

  let res = await send();

  // On a 401, attempt ONE silent refresh-and-retry (the access token likely just expired) before
  // giving up. /auth/* calls never retry — they are the refresh path itself.
  if (res.status === 401 && !path.startsWith("/auth/")) {
    const newToken = await refreshAccessToken();
    if (newToken) res = await send();
  }

  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized();
      return new Promise<T>(() => {});
    }
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail ?? `HTTP ${res.status}`, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Auth — served by the identity service.
export const login = (email: string, password: string) =>
  request<TokenResponse>(
    "/auth/token",
    {
      method: "POST",
      body: JSON.stringify({ email, password }),
    },
    IDENTITY_BASE,
  );

// Single-flight silent refresh: exchange the httpOnly refresh cookie for a new access token.
// Concurrent callers (a burst of 401s, the __root reload guard) share one in-flight request.
// Does NOT route through `request` — it is the refresh path and must not trigger its 401 retry.
// Returns the new access token, or null if the cookie is missing/expired/rotated.
let _refreshInFlight: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`${IDENTITY_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = (await res.json()) as TokenResponse;
      setAccessToken(data.access_token);
      return data.access_token;
    } catch {
      return null;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

// Revoke the refresh token server-side and clear the cookie; then drop the in-memory access token.
export async function logout(): Promise<void> {
  try {
    await fetch(`${IDENTITY_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Best-effort — clear locally even if the network call fails.
  }
  setAccessToken(null);
}

export const getMe = () => request<UserMe>("/auth/users/me", {}, IDENTITY_BASE);

// Inventory
export interface ListBuildingsParams {
  city?: string;
  type?: BuildingType;
  status?: BuildingStatus;
}

export const listBuildings = ({
  city,
  type,
  status,
}: ListBuildingsParams = {}) => {
  const params = new URLSearchParams();
  if (city) params.set("city", city);
  if (type) params.set("type", type);
  if (status) params.set("status", status);
  const qs = params.toString();
  return request<Building[]>(`/buildings${qs ? `?${qs}` : ""}`);
};

export const getBuilding = (id: string) =>
  request<Building>(`/buildings/${id}`);
export const getBuildingSummary = (id: string) =>
  request<BuildingSummary>(`/buildings/${id}/summary`);

// Zones
export const listZones = (buildingId: string) =>
  request<Zone[]>(`/buildings/${buildingId}/zones`);
export const getZone = (buildingId: string, zoneId: string) =>
  request<Zone>(`/buildings/${buildingId}/zones/${zoneId}`);

export async function createZone(
  buildingId: string,
  data: {
    name: string;
    floor: number;
    icon?: string;
    zone_type?: string | null;
    description?: string;
  },
): Promise<Zone> {
  const res = await fetch(`${BASE}/buildings/${buildingId}/zones`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Zone>;
}

export async function updateZone(
  buildingId: string,
  zoneId: string,
  data: {
    name?: string;
    floor?: number;
    icon?: string;
    zone_type?: string | null;
    description?: string;
  },
): Promise<Zone> {
  const res = await fetch(`${BASE}/buildings/${buildingId}/zones/${zoneId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Zone>;
}

export async function deleteZone(
  buildingId: string,
  zoneId: string,
): Promise<void> {
  const res = await fetch(`${BASE}/buildings/${buildingId}/zones/${zoneId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
}

export type EquipmentType =
  | "air_handling_unit"
  | "heat_pump"
  | "chiller"
  | "lighting_system"
  | "hot_water_unit"
  | "refrigeration_unit"
  | "district_heating_substation"
  | "boiler"
  | "pump_group"
  | "heat_exchanger"
  | "electricity_meter"
  | "heat_meter"
  | "temperature_sensor"
  | "chilled_beam";

// Building locations (managed list of physical installation locations)
export const getBuildingLocations = (buildingId: string) =>
  request<string[]>(`/buildings/${buildingId}/locations`);

export async function updateBuildingLocations(
  buildingId: string,
  locations: string[],
): Promise<string[]> {
  const res = await fetch(`${BASE}/buildings/${buildingId}/locations`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(locations),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<string[]>;
}

// Equipment
export const listEquipment = (buildingId: string) =>
  request<Equipment[]>(`/buildings/${buildingId}/equipment`);

export async function createEquipment(
  buildingId: string,
  data: {
    name: string;
    equipment_type: string;
    zone_id?: string | null;
    location?: string | null;
    equipment_tag?: string;
    state?: Record<string, unknown>;
  },
): Promise<Equipment> {
  const res = await fetch(`${BASE}/buildings/${buildingId}/equipment`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Equipment>;
}

export async function updateEquipment(
  buildingId: string,
  equipmentId: string,
  data: {
    name?: string;
    equipment_type?: string;
    zone_id?: string | null;
    zone_ids?: string[];
    location?: string | null;
    equipment_tag?: string;
    state?: Record<string, unknown>;
    optimization_enabled?: boolean;
    declared_variants?: string[];
    declared_data_points?: string[];
    declared_component_order?: string[];
    declared_component_subtypes?: Record<string, string>;
    declared_stream_component_order?: Record<string, string[]>;
  },
): Promise<Equipment> {
  const res = await fetch(
    `${BASE}/buildings/${buildingId}/equipment/${equipmentId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Equipment>;
}

// Rewrite the taught source→target pairing this equipment is actually fed from (source signal ->
// KB data point id). Whole-map replace; Building resolves which store to write (csv_signal_schemas
// vs the device connector's signal_schema) and derives declared_data_points from the result
// (docs/reference/decisions.md ADR-017) — the frontend never sends declared_data_points itself.
export const putEquipmentSignalMap = (
  buildingId: string,
  equipmentId: string,
  signalMap: Record<string, string>,
): Promise<Equipment> =>
  request<Equipment>(
    `/buildings/${buildingId}/equipment/${equipmentId}/signal-map`,
    {
      method: "PUT",
      body: JSON.stringify({ signal_map: signalMap }),
    },
  );

// Config parameters — operator-entered KB values (e.g. contracted peak kW). The KB declares the
// bounds; the API rejects an out-of-range value with a 422 whose `detail` explains why, which
// `request` surfaces as the thrown Error's message so the caller can show it inline.
export interface ConfigParameterValue {
  parameter_id: string;
  name_fi: string;
  description_fi: string;
  unit: string;
  value_type: string;
  minimum: number | null;
  maximum: number | null;
  value: number | null;
  set_by: string | null;
  set_at: string | null;
}

export const listEquipmentConfigParameters = (
  buildingId: string,
  equipmentId: string,
): Promise<ConfigParameterValue[]> =>
  request<ConfigParameterValue[]>(
    `/buildings/${buildingId}/equipment/${equipmentId}/config-parameters`,
  );

export const setEquipmentConfigParameter = (
  buildingId: string,
  equipmentId: string,
  parameterId: string,
  value: number,
): Promise<ConfigParameterValue> =>
  request<ConfigParameterValue>(
    `/buildings/${buildingId}/equipment/${equipmentId}/config-parameters/${encodeURIComponent(
      parameterId,
    )}`,
    { method: "PUT", body: JSON.stringify({ value }) },
  );

// Context-setpoint suggest -> accept -> effective (UC4, story #922). One entry per
// context-setpoint quantity family the equipment's KB type declares — empty for equipment
// with none. Building resolves it from its own data: the published type definition, the
// newest device reading, the learned suggestion and the accepted-value store
// (docs/spec/knowledge-base.md § Context setpoints — suggestion and accept).
export type ContextSetpointEffectiveSource =
  "device" | "accepted" | "manual" | "unwired";

export interface ContextSetpoint {
  context_setpoint_id: string;
  name_fi: string;
  unit: string;
  device_present: boolean;
  suggested_value: number | null;
  suggested_unit: string | null;
  accepted_value: number | null;
  accepted_set_by: string | null;
  accepted_set_at: string | null; // ISO datetime
  effective_source: ContextSetpointEffectiveSource;
  effective_value: number | null;
}

export const listEquipmentContextSetpoints = (
  buildingId: string,
  equipmentId: string,
) =>
  request<ContextSetpoint[]>(
    `/buildings/${buildingId}/equipment/${equipmentId}/context-setpoints`,
  );

// Context-setpoint accept (UC4, story #922) — the stored accepted value. Empty request body by
// design: the endpoint always re-resolves the current suggestion rather than trusting a
// client-supplied value (docs/spec/knowledge-base.md § Context setpoints —
// suggestion and accept → The accept endpoint).
// `origin` distinguishes how the value entered this slot: "accepted" (accept endpoint below) vs
// "manual" (the typed-fill endpoint, story #1526/#1529) — both share the one
// equipment_accepted_setpoints row per (equipment_id, context_setpoint_id).
export type ContextSetpointOrigin = "accepted" | "manual";

export interface AcceptedContextSetpoint {
  equipment_id: string;
  context_setpoint_id: string;
  value: number;
  unit: string | null;
  set_by: string | null;
  set_at: string; // ISO datetime
  origin: ContextSetpointOrigin;
}

export const acceptContextSetpointSuggestion = (
  buildingId: string,
  equipmentId: string,
  contextSetpointId: string,
): Promise<AcceptedContextSetpoint> =>
  request<AcceptedContextSetpoint>(
    `/buildings/${buildingId}/equipment/${equipmentId}/context-setpoints/${encodeURIComponent(
      contextSetpointId,
    )}/accept`,
    { method: "POST" },
  );

// Operator-typed value for an UNMAPPED context setpoint (story #1526's typed-fill write path) —
// persists into the same slot `acceptContextSetpointSuggestion` writes, with `origin="manual"`.
// Refuses (400) when the setpoint has a live device point; this is local analytics only, never a
// write-through to the BMS/device (docs/spec/knowledge-base.md § Context setpoints).
export const setManualContextSetpointValue = (
  buildingId: string,
  equipmentId: string,
  contextSetpointId: string,
  value: number,
  unit: string | null,
): Promise<AcceptedContextSetpoint> =>
  request<AcceptedContextSetpoint>(
    `/buildings/${buildingId}/equipment/${equipmentId}/context-setpoints/${encodeURIComponent(
      contextSetpointId,
    )}/value`,
    { method: "PUT", body: JSON.stringify({ value, unit }) },
  );

export async function deleteEquipment(
  buildingId: string,
  equipmentId: string,
): Promise<void> {
  const res = await fetch(
    `${BASE}/buildings/${buildingId}/equipment/${equipmentId}`,
    {
      method: "DELETE",
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
}

export const getZoneExpectedBehaviors = (buildingId: string, zoneId: string) =>
  request<ZoneExpectedBehaviors>(
    `/buildings/${buildingId}/zones/${zoneId}/expected-behaviors`,
  );

export const putZoneExpectedBehaviors = (
  buildingId: string,
  zoneId: string,
  items: ZoneExpectedBehaviorItem[],
) =>
  request<ZoneExpectedBehaviors>(
    `/buildings/${buildingId}/zones/${zoneId}/expected-behaviors`,
    {
      method: "PUT",
      body: JSON.stringify({ items }),
    },
  );

// Recipes
export const listRecipes = (
  buildingId: string,
  status?: RecipeStatus,
  equipmentId?: string,
) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (equipmentId) params.set("equipment_id", equipmentId);
  const qs = params.toString();
  return request<Recipe[]>(
    `/buildings/${buildingId}/recipes${qs ? `?${qs}` : ""}`,
  );
};

export async function getLatestRecipe(
  buildingId: string,
  equipmentId?: string,
): Promise<Recipe | null> {
  try {
    const recipes = await listRecipes(buildingId, undefined, equipmentId);
    if (recipes.length === 0) return null;
    return recipes.reduce((a, b) => (a.created_at > b.created_at ? a : b));
  } catch {
    return null;
  }
}
export const getRecipe = (buildingId: string, recipeId: string) =>
  request<Recipe>(`/buildings/${buildingId}/recipes/${recipeId}`);
// Recipes apply autonomously within the building's limits (ADR-013) — there is no
// human accept/reject/apply/simulate action.

// Buildings
export interface CreateBuildingPayload {
  name: string;
  address: string;
  city: string;
  building_type: BuildingType;
  // Explicit coordinates win over geocode-on-create; omit both to let the backend resolve them
  // from `address`/`city` (#1360, ADR-018) — never send a fabricated/placeholder value.
  lat?: number;
  lon?: number;
}

export const createBuilding = (data: CreateBuildingPayload) =>
  request<Building>("/buildings", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateBuilding = (
  id: string,
  data: {
    status?: BuildingStatus;
    name?: string;
    live_optimization_enabled?: boolean;
    address?: string;
    city?: string;
    // Explicit coordinates win over re-geocoding. Omit both (rather than sending `null`) to
    // mean "let the backend geocode from the new address/city, or leave existing coordinates
    // alone if the address didn't change" — the API has no separate "clear coordinates" action.
    lat?: number;
    lon?: number;
  },
) =>
  request<Building>(`/buildings/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

// Building groups (Kiinteistösalkut)

export interface BuildingGroup {
  id: string;
  organization_id: string;
  name: string;
  building_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface BuildingGroupStats {
  group_id: string;
  building_count: number;
  total_optimization_potential_eur_month: number;
  alert_count: number;
  warn_count: number;
  ok_count: number;
}

export const listGroups = () => request<BuildingGroup[]>("/groups");

export const getGroup = (id: string) => request<BuildingGroup>(`/groups/${id}`);

export const createGroup = (name: string) =>
  request<BuildingGroup>("/groups", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const renameGroup = (id: string, name: string) =>
  request<BuildingGroup>(`/groups/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });

export async function deleteGroup(id: string): Promise<void> {
  const res = await fetch(`${BASE}/groups/${id}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
}

export const addBuildingToGroup = (groupId: string, buildingId: string) =>
  request<BuildingGroup>(`/groups/${groupId}/buildings/${buildingId}`, {
    method: "PUT",
  });

export const removeBuildingFromGroup = (groupId: string, buildingId: string) =>
  request<BuildingGroup>(`/groups/${groupId}/buildings/${buildingId}`, {
    method: "DELETE",
  });

export const getGroupStats = (groupId: string) =>
  request<BuildingGroupStats>(`/groups/${groupId}/stats`);

export async function deleteBuilding(id: string): Promise<void> {
  const res = await fetch(`${BASE}/buildings/${id}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
}

// Building profile
export interface EquipmentCapability {
  equipment_id: string;
  name: string;
  equipment_type: string;
  controllable: boolean;
  unit: string | null;
  min_value: number | null;
  max_value: number | null;
  current_state: Record<string, unknown>;
}

export interface BuildingProfile {
  building_id: string;
  equipment: EquipmentCapability[];
  available_measurements: string[];
  updated_at: string;
}

export const getProfile = (buildingId: string) =>
  request<BuildingProfile>(`/buildings/${buildingId}/profile`);

// Analytics
export const getAnalytics = (buildingId: string, days = 7) =>
  request<BuildingAnalytics>(`/buildings/${buildingId}/analytics?days=${days}`);

// Replay
export interface ReplayCreate {
  from_ts: string; // ISO datetime
  to_ts: string; // ISO datetime
}

export interface Replay {
  id: string;
  building_id: string;
  from_ts: string;
  to_ts: string;
  status: "pending" | "running" | "completed" | "failed";
  window_count: number;
  completed_windows: number;
  created_at: string;
  updated_at: string;
}

export interface WindowDiff {
  window_start: string;
  window_end: string;
  actual_energy_kwh: number;
  optimised_energy_kwh: number;
  delta_pct: number;
}

export interface ReplayDiff {
  replay_id: string;
  building_id: string;
  total_actual_energy_kwh: number;
  total_optimised_energy_kwh: number;
  estimated_savings_eur_month: number;
  windows: WindowDiff[];
}

export const createReplay = (buildingId: string, data: ReplayCreate) =>
  request<Replay>(`/buildings/${buildingId}/replay`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getReplay = (buildingId: string, replayId: string) =>
  request<Replay>(`/buildings/${buildingId}/replay/${replayId}`);

export const getReplayDiff = (buildingId: string, replayId: string) =>
  request<ReplayDiff>(`/buildings/${buildingId}/replay/${replayId}/diff`);

// Historical data intake
export interface IntakeResult {
  accepted: number;
  skipped: number;
  preview: {
    id: string;
    building_id: string;
    zone_id: string | null;
    electricity_kwh: number | null;
    heat_kwh: number | null;
    water_m3: number | null;
    indoor_temperature: number | null;
    co2_ppm: number | null;
    recorded_at: string;
    created_by: string;
    created_at: string;
  }[];
}

export interface ChannelSummary {
  electricity: boolean;
  heat: boolean;
  temperature: boolean;
  co2: boolean;
  water: boolean;
}

export interface NormalizedReadingPreview {
  recorded_at: string;
  interval_minutes: number;
  electricity_kwh: number | null;
  heat_kwh: number | null;
  indoor_temperature: number | null;
  co2_ppm: number | null;
  water_m3: number | null;
}

export interface RawColumn {
  name: string;
  sample_values: string[];
  is_numeric: boolean;
  kairos_field: string | null;
  /** Always populated: the resolved channel, or "Ei tunnistettu". */
  mapping_label: string;
  /** Why a recognised column was deliberately skipped, when the mapper can say. */
  mapping_note: string | null;
}

export interface CsvAnalysis {
  format_id: string;
  format_label: string;
  estimated_rows: number;
  date_from: string | null;
  date_to: string | null;
  interval_minutes: number;
  channels: ChannelSummary;
  preview: NormalizedReadingPreview[];
  warnings: string[];
  raw_columns: RawColumn[];
}

async function uploadFile<T>(
  path: string,
  file: File,
  extraQs = "",
  extraFields?: Record<string, string>,
): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  for (const [key, val] of Object.entries(extraFields ?? {})) {
    formData.append(key, val);
  }
  // Token comes from the single accessor — no Content-Type here, so FormData sets its own boundary.
  const res = await fetch(`${BASE}${path}${extraQs}`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

/** Device simulated connector as relayed by Building (no raw CSV bytes). */
export interface SimulatedConnector {
  id: string;
  building_id: string;
  equipment_id: string | null;
  connector_type: string;
  label: string;
  signal_schema: Record<string, string>;
  has_source_csv: boolean;
  unmapped_signals: string[];
  created_at: string;
  updated_at: string;
}

export interface SimulatedFeedStatus {
  connector_id: string;
  running: boolean;
  signal_schema?: Record<string, string>;
  unmapped_signals?: string[];
}

export const ensureSimulatedConnector = (
  buildingId: string,
  equipmentId: string,
  file: File,
): Promise<SimulatedConnector> =>
  uploadFile<SimulatedConnector>(
    `/buildings/${buildingId}/equipment/${equipmentId}/simulated-connector`,
    file,
  );

export const deleteSimulatedConnector = (
  buildingId: string,
  equipmentId: string,
): Promise<void> =>
  request<void>(
    `/buildings/${buildingId}/equipment/${equipmentId}/simulated-connector`,
    { method: "DELETE" },
  );

export async function replaceSimulatedConnectorCsv(
  buildingId: string,
  equipmentId: string,
  file: File,
): Promise<SimulatedConnector> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(
    `${BASE}/buildings/${buildingId}/equipment/${equipmentId}/simulated-connector/source-csv`,
    { method: "PUT", headers: authHeaders(), body: formData },
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(
      typeof err.detail === "string" ? err.detail : `HTTP ${res.status}`,
    );
  }
  return res.json() as Promise<SimulatedConnector>;
}

export const startSimulatedFeed = (
  buildingId: string,
  equipmentId: string,
): Promise<SimulatedFeedStatus> =>
  request<SimulatedFeedStatus>(
    `/buildings/${buildingId}/equipment/${equipmentId}/simulated-connector/start`,
    { method: "POST" },
  );

export const stopSimulatedFeed = (
  buildingId: string,
  equipmentId: string,
): Promise<SimulatedFeedStatus> =>
  request<SimulatedFeedStatus>(
    `/buildings/${buildingId}/equipment/${equipmentId}/simulated-connector/stop`,
    { method: "POST" },
  );

/** `null` when the equipment has no simulated connector (Building 404). */
export async function getSimulatedFeedStatus(
  buildingId: string,
  equipmentId: string,
): Promise<SimulatedFeedStatus | null> {
  const res = await fetch(
    `${BASE}/buildings/${buildingId}/equipment/${equipmentId}/simulated-connector/status`,
    { headers: authHeaders() },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(
      typeof err.detail === "string" ? err.detail : `HTTP ${res.status}`,
    );
  }
  return res.json() as Promise<SimulatedFeedStatus>;
}

export const analyzeCsvData = (
  buildingId: string,
  file: File,
): Promise<CsvAnalysis> =>
  uploadFile<CsvAnalysis>(
    `/buildings/${buildingId}/data/historical/analyze`,
    file,
  );

export interface MeasurementRecord {
  id: string;
  building_id: string;
  zone_id: string | null;
  electricity_kwh: number | null;
  heat_kwh: number | null;
  water_m3: number | null;
  indoor_temperature: number | null;
  co2_ppm: number | null;
  recorded_at: string;
  created_by: string;
  created_at: string;
}

export const listMeasurements = (
  buildingId: string,
  limit = 2000,
  offset = 0,
  fromTs?: string,
) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (fromTs) params.set("from_ts", fromTs);
  return request<MeasurementRecord[]>(
    `/buildings/${buildingId}/data/measurements?${params}`,
  );
};

export interface ChannelStats {
  count: number;
  min: number;
  max: number;
  avg: number;
}

export interface MeasurementStats {
  building_id: string;
  total_rows: number;
  from_ts: string | null;
  to_ts: string | null;
  electricity_kwh: ChannelStats | null;
  heat_kwh: ChannelStats | null;
  water_m3: ChannelStats | null;
  indoor_temperature: ChannelStats | null;
  co2_ppm: ChannelStats | null;
}

export type StrategyField =
  | "consumption_kwh_m2_year"
  | "comfort_temp_min"
  | "comfort_temp_max"
  | "co2_limit_ppm"
  | "peak_power_cap_kw"
  | "savings_goal_eur_year";

/** One käyttöaste step's strategy bounds (story #1758 — docs/spec/knowledge-base.md § Zone KPI
 *  steps -> Bounds decision). Every field is optional: an unset one is not a constraint at this
 *  step — it defers to the live `comfort_temp_min`/`comfort_temp_max`/`co2_limit_ppm` fields on
 *  `BuildingTarget`, which double as the "busy" step's default. Never silently defaulted to a
 *  number the strategy did not set (ADR-020). Mirrors the backend's `StrategyStepBound`
 *  (services/building/.../domain/models.py) exactly.
 */
export interface StrategyStepBound {
  temp_min: number | null;
  temp_max: number | null;
  co2_limit_ppm: number | null;
}

export interface BuildingTarget {
  id: string;
  building_id: string;
  zone_id: string | null;
  consumption_kwh_m2_year: number | null;
  comfort_temp_min: number | null;
  comfort_temp_max: number | null;
  co2_limit_ppm: number | null;
  peak_power_cap_kw: number | null;
  savings_goal_eur_year: number | null;
  /** Story #1758's per-käyttöaste-step bounds table, keyed by step id (`unoccupied` | `partial`
   *  | `busy`). Replaces the retired comfort-min/max pair that was keyed on a binary occupied
   *  flag. `comfort_temp_min`/`comfort_temp_max`/`co2_limit_ppm` above are extended, not
   *  replaced — they double as the "busy" step's implicit default whenever that step's own entry
   *  (or field) is unset. */
  step_bounds: Record<string, StrategyStepBound>;
  updated_at: string;
}

export interface StrategyProposal {
  id: string;
  building_id: string;
  source_agent_id: string;
  field: StrategyField;
  current_value: number | null;
  proposed_value: number | null;
  justification_fi: string;
  status: "pending" | "accepted" | "rejected";
  created_at: string;
  resolved_at: string | null;
}

export interface RecommendationResult {
  recommendations_fi: string;
  strategy_proposals: StrategyProposal[];
  external_context: Record<string, unknown>;
}

export const getMeasurementStats = (buildingId: string, days?: number) => {
  const params = days != null ? `?days=${days}` : "";
  return request<MeasurementStats>(
    `/buildings/${buildingId}/data/measurements/stats${params}`,
  );
};

/**
 * Which `BuildingTarget` limits bound a signal of a given display unit. Static schema metadata
 * owned by building domain — a consumer bands a trend from this rather than naming limit fields
 * it does not own.
 */
export interface StrategyBoundUnit {
  unit: string;
  min_field: StrategyField | null;
  max_field: StrategyField | null;
}

export const listStrategyBoundUnits = () =>
  request<StrategyBoundUnit[]>("/buildings/strategy/bound-units");

export const getStrategy = (buildingId: string) =>
  request<BuildingTarget | null>(`/buildings/${buildingId}/strategy`);

export const updateStrategy = (
  buildingId: string,
  data: Partial<Record<StrategyField, number | null>> & {
    step_bounds?: Record<string, StrategyStepBound>;
  },
) =>
  request<BuildingTarget>(`/buildings/${buildingId}/strategy`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const listStrategyProposals = (buildingId: string, status?: string) => {
  const qs = status ? `?status=${status}` : "";
  return request<StrategyProposal[]>(
    `/buildings/${buildingId}/strategy/proposals${qs}`,
  );
};

// URL-scoped collection query (Suositukset, bug #1348) — one request whose query string is the
// browser URL's own scope (building/group/city/type/status), resolved server-side (#1347), plus
// optional proposal `status`. Never a caller-resolved building_ids list.
export function strategyProposalsCollectionPath(
  scope: InventorySearch,
  status?: string,
): string {
  const q = new URLSearchParams();
  if (scope.building) q.set("building", scope.building);
  if (scope.group) q.set("group", scope.group);
  if (scope.city) q.set("city", scope.city);
  if (scope.type) q.set("type", scope.type);
  if (scope.status) q.set("building_status", scope.status);
  if (status) q.set("status", status);
  const qs = q.toString();
  return `/strategy/proposals${qs ? `?${qs}` : ""}`;
}

export const listStrategyProposalsForScope = (
  scope: InventorySearch,
  status?: string,
): Promise<StrategyProposal[]> =>
  request<StrategyProposal[]>(strategyProposalsCollectionPath(scope, status));

export const acceptStrategyProposal = (
  buildingId: string,
  proposalId: string,
) =>
  request<StrategyProposal>(
    `/buildings/${buildingId}/strategy/proposals/${proposalId}/accept`,
    { method: "POST" },
  );

export const rejectStrategyProposal = (
  buildingId: string,
  proposalId: string,
) =>
  request<StrategyProposal>(
    `/buildings/${buildingId}/strategy/proposals/${proposalId}/reject`,
    { method: "POST" },
  );

export const getRecommendations = (buildingId: string) =>
  request<RecommendationResult>(`/buildings/${buildingId}/recommendations`, {
    method: "POST",
  });

export async function getLatestRecommendationResult(
  buildingId: string,
): Promise<RecommendationResult | null> {
  try {
    return await request<RecommendationResult>(
      `/buildings/${buildingId}/recommendations`,
    );
  } catch {
    return null;
  }
}

export async function uploadHistoricalData(
  buildingId: string,
  file: File,
  dataContext: "baseline" | "operational",
  zoneId?: string,
): Promise<IntakeResult> {
  return uploadFile<IntakeResult>(
    `/buildings/${buildingId}/data/historical`,
    file,
    zoneId ? `?zone_id=${zoneId}` : "",
    { data_context: dataContext },
  );
}

// ── Data point history ────────────────────────────────────────────────────────

export type DataPointDomain =
  "talotekniikka" | "palvelut" | "ennuste" | "optimointi" | "simulointi";
export type DataPointNature =
  | "sensor"
  | "setpoint"
  | "actuator"
  | "status"
  | "calculated"
  | "forecast"
  | "marker";

export interface DataPointNameMeta {
  name: string;
  unit: string | null;
  domain: DataPointDomain;
  nature: DataPointNature;
  label_fi: string | null;
  component_id: string | null;
  kind: string | null;
  order: number | null;
  connection_source: string | null;
  connection_upstream_name: string | null;
}

export interface DataPointSource {
  source_id: string;
  source_type: "zone" | "equipment" | "building";
  source_name: string;
  available_names: DataPointNameMeta[];
  equipment_type: string | null;
}

export interface DataPointSample {
  source_type: "zone" | "equipment" | "building";
  source_id: string;
  name: string;
  numeric_value: number | null;
  text_value?: string | null;
  unit: string | null;
  domain: DataPointDomain;
  nature: DataPointNature;
  recorded_at: string;
  is_baseline: boolean;
}

export const listDataPointSources = (
  buildingId: string,
): Promise<DataPointSource[]> =>
  request<DataPointSource[]>(`/buildings/${buildingId}/data-points/sources`);

/** Building-owned CSV file-import map: raw column → canonical data_point id. */
export interface CsvSignalSchemaOut {
  equipment_id: string | null;
  zone_id: string | null;
  signal_schema: Record<string, string>;
}

export const listCsvSignalSchemas = (
  buildingId: string,
): Promise<CsvSignalSchemaOut[]> =>
  request<CsvSignalSchemaOut[]>(`/buildings/${buildingId}/csv-signal-schemas`);

/** Device-owned live/simulated-connector map — the connector-fed half of the
 *  same source→target picture `listCsvSignalSchemas` covers for file imports
 *  (ADR-017). `putEquipmentSignalMap` targets whichever store the equipment
 *  actually uses, so a reader must let this one win per `equipment_id` (#1519
 *  changes-requested). */
export interface ConnectorSignalSchemaOut {
  equipment_id: string;
  signal_schema: Record<string, string>;
}

export const listConnectorSignalSchemas = (
  buildingId: string,
): Promise<ConnectorSignalSchemaOut[]> =>
  request<ConnectorSignalSchemaOut[]>(
    `/buildings/${buildingId}/connector-signal-schemas`,
  );

/** KB matcher ehdotus for raw Lähdesignaali names (same matcher as CSV upload). */
export interface SignalMapSuggestion {
  bms_name: string;
  data_point_id: string;
  component_id: string;
  confidence: number;
}

export const suggestEquipmentSignalMap = (
  buildingId: string,
  equipmentId: string,
  bmsPoints: string[],
): Promise<SignalMapSuggestion[]> =>
  request<SignalMapSuggestion[]>(
    `/buildings/${buildingId}/equipment/${equipmentId}/signal-map/suggestions`,
    {
      method: "POST",
      body: JSON.stringify({ bms_points: bmsPoints }),
    },
  );

export const getDataPointHistory = (
  buildingId: string,
  sourceIds: string[],
  names: string[],
  fromTs?: string,
  limit = 5000,
): Promise<DataPointSample[]> => {
  const params = new URLSearchParams({ limit: String(limit) });
  for (const id of sourceIds) params.append("source_ids", id);
  for (const n of names) params.append("names", n);
  if (fromTs) params.set("from_ts", fromTs);
  return request<DataPointSample[]>(
    `/buildings/${buildingId}/data-points/history?${params}`,
  );
};

/** Energiatehokkuus building view — one shaped payload for one selected equipment: real,
 *  already-bucketed series (real gaps are `null`, never fabricated) for the building's external
 *  signals, the equipment's served zones, and the equipment's own recipe history. See
 *  services/building/.../domain/services/operational_loop.py's own docstring for why this
 *  shaping lives server-side — the frontend only renders what it's given. */
export interface OperationalLoopSeries {
  name: string;
  unit: string | null;
  values: (number | null)[];
  /** Populated only for a recipe series — KB-sourced display name, never a hardcoded
   *  per-equipment-type label. `null` for external/internal series. */
  label: string | null;
  /** Recipe series only, `null` otherwise: "optimized" (what the recipe wrote to the port),
   *  "measured" (the port's KB `baseline_of` sensor — what the equipment actually did) or
   *  "baseline" (derived server-side: the measurement held from just before each recipe
   *  applied — the perusura). */
  role: string | null;
  /** One bool per grid step, `true` where that step's value is pattern-derived (same-weekday/
   *  hour average) rather than a real reading. Non-empty only for the occupancy-family signals
   *  the weekly pattern is legitimate for (`occupancy`, `occupancy_count`,
   *  `kb_zone_kpi_occupancy`) — every other series keeps an honest hole on its future side
   *  instead (task #1884). The frontend must read this to say "pattern-derived", never infer
   *  it from `now_index`. */
  pattern_filled: boolean[];
}

/** One applied recipe's span on the loop's step grid — `[start_index, end_index)`. */
export interface OperationalLoopRecipeWindow {
  recipe_id: string;
  mechanism: OptimizationMechanism | null;
  applied_at: string | null;
  start_index: number;
  end_index: number;
  /** The last commanded value per port (RecipeAction.parameter → baseline + offset). */
  targets: Record<string, number>;
}

/** A strategy bound (BuildingTarget) a served zone's signal must respect while a recipe is in
 *  force — `signal` is an internal series name ("co2" | "temperature"). */
export interface OperationalLoopConstraint {
  signal: string;
  comparator: "<=" | ">=";
  threshold: number;
  unit: string | null;
  source: string;
}

/** Counts over the history-side steps inside some recipe window; a step is "checked" only when
 *  the real signals to judge it existed for that bucket. `effect_kwh` is null when the fan's KB
 *  nameplate power isn't configured — no nameplate, no kW claimed. */
export interface OperationalLoopVerification {
  active_steps: number;
  control_checked_steps: number;
  control_followed_steps: number;
  constraint_checked_steps: number;
  constraint_ok_steps: number;
  effect_kwh: number | null;
}

export interface OperationalLoopZone {
  zone_id: string;
  zone_name: string;
  series: OperationalLoopSeries[];
  /** Story #1758 (task #1764): the same käyttöaste/sisäilman laatu/lämpötila KPI triple
   *  Signaalit and the Sisäilmaolosuhteet zone card read (agent's zone-KPI contract, task
   *  #1761) — additive beside `series`, which stays exactly as it was: same names, same
   *  values, same shape. See @/lib/zone-kpi.ts for the shared reader all three views use. */
  occupancy: ZoneKpi;
  air_quality: ZoneKpi;
  temperature: ZoneKpi;
  /** Persisted −100…+100 KPI scores on the loop grid, weekly-pattern filled after now. */
  kpi?: OperationalLoopSeries[];
}

export interface OperationalLoopWindow {
  anchor: string; // ISO instant index 0 represents
  step_minutes: number;
  past_steps: number;
  future_steps: number;
  now_index: number;
}

export interface OperationalLoopReport {
  equipment_id: string;
  equipment_name: string;
  equipment_type: string;
  window: OperationalLoopWindow;
  external: OperationalLoopSeries[];
  internal: OperationalLoopZone[];
  /** Empty when this equipment has no real recipe history yet. The view shows unavailable,
   *  never a client-side demo curve. */
  recipe: OperationalLoopSeries[];
  /** Fan electrical power from the affinity law — computed by the building service.
   *  Offset-native ventilation_level_offset is % of scheduled fan power; leftover absolute
   *  speed is % of nameplate. Empty when this equipment has no % port
   *  (`impact_empty_reason: "no_fan_port"`). Carries a parallel `fan_electrical_kwh`
   *  optimized/baseline pair only when the equipment's `rated_supply_fan_power_kw` nameplate
   *  is configured *and* the port is an absolute speed — never a zero-filled kWh series
   *  in its place. */
  impact?: OperationalLoopSeries[];
  impact_empty_reason?: string | null;
  /** Terms Yhteisvaikutus cannot estimate for this equipment yet — heating/cooling/heat_recovery
   *  always (no what-if evaluator, ADR-020), plus "fan_electrical" when
   *  `impact_empty_reason === "no_fan_port"`. ImpactChart labels these *ei vielä laskettavissa*
   *  from this list instead of a hardcoded set of its own. */
  not_computable_terms: string[];
  /** Signal names the KB's optimization services read for this equipment type — what the view
   *  foregrounds. Empty = the KB declares no focus, not "nothing matters". */
  focus: string[];
  recipe_windows: OperationalLoopRecipeWindow[];
  constraints: OperationalLoopConstraint[];
  /** null when there is no real recipe series at all — nothing to verify against. */
  verification: OperationalLoopVerification | null;
}

export const getOperationalLoop = (
  buildingId: string,
  equipmentId: string,
): Promise<OperationalLoopReport> =>
  request<OperationalLoopReport>(
    `/buildings/${buildingId}/equipment/${equipmentId}/operational-loop`,
  );

export const getLatestDataPoints = (
  buildingId: string,
  sourceIds?: string[],
): Promise<DataPointSample[]> => {
  const params = new URLSearchParams();
  for (const id of sourceIds ?? []) params.append("source_ids", id);
  const qs = params.toString();
  return request<DataPointSample[]>(
    `/buildings/${buildingId}/data-points/latest${qs ? `?${qs}` : ""}`,
  );
};

export interface DataPointSampleWrite {
  source_type: "zone" | "equipment" | "building";
  source_id: string;
  name: string;
  numeric_value?: number | null;
  text_value?: string | null;
  unit?: string | null;
  domain: DataPointDomain;
  nature: DataPointNature;
  recorded_at: string;
  is_baseline?: boolean;
}

export const postDataPoints = (
  buildingId: string,
  samples: DataPointSampleWrite[],
): Promise<void> =>
  request<void>(`/buildings/${buildingId}/data-points`, {
    method: "POST",
    body: JSON.stringify(samples),
  });

// Building services
export type BuildingServiceType =
  | "weather-preconditioning"
  | "electricity-price-optimization"
  | "carbon-intensity"
  | "demand-response";

export interface BuildingService {
  id: string;
  building_id: string;
  service_type: BuildingServiceType;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export const listBuildingServices = (
  buildingId: string,
): Promise<BuildingService[]> =>
  request<BuildingService[]>(`/buildings/${buildingId}/services`);

export const updateBuildingService = (
  buildingId: string,
  serviceType: BuildingServiceType,
  isActive: boolean,
): Promise<BuildingService> =>
  request<BuildingService>(`/buildings/${buildingId}/services/${serviceType}`, {
    method: "PATCH",
    body: JSON.stringify({ is_active: isActive }),
  });

// Equipment zone links
export const addEquipmentZoneLink = (
  buildingId: string,
  equipmentId: string,
  zoneId: string,
): Promise<void> =>
  request<void>(
    `/buildings/${buildingId}/equipment/${equipmentId}/zone-links`,
    {
      method: "POST",
      body: JSON.stringify({ zone_id: zoneId }),
    },
  );

export const removeEquipmentZoneLink = (
  buildingId: string,
  equipmentId: string,
  zoneId: string,
): Promise<void> =>
  request<void>(
    `/buildings/${buildingId}/equipment/${equipmentId}/zone-links/${zoneId}`,
    {
      method: "DELETE",
    },
  );

// Equipment dependencies
export type DependencyType = "feeds";

export interface EquipmentDependency {
  id: string;
  building_id: string;
  source_equipment_id: string;
  target_equipment_id: string;
  dependency_type: DependencyType;
  created_at: string;
}

export interface EquipmentDependencyCreate {
  source_equipment_id: string;
  target_equipment_id: string;
  dependency_type: DependencyType;
}

export const listEquipmentDependencies = (
  buildingId: string,
): Promise<EquipmentDependency[]> =>
  request<EquipmentDependency[]>(
    `/buildings/${buildingId}/equipment-dependencies`,
  );

export const createEquipmentDependency = (
  buildingId: string,
  data: EquipmentDependencyCreate,
): Promise<EquipmentDependency> =>
  request<EquipmentDependency>(
    `/buildings/${buildingId}/equipment-dependencies`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );

export const deleteEquipmentDependency = (
  buildingId: string,
  depId: string,
): Promise<void> =>
  request<void>(`/buildings/${buildingId}/equipment-dependencies/${depId}`, {
    method: "DELETE",
  });

export const deleteEquipmentDependencyByPair = (
  buildingId: string,
  sourceId: string,
  targetId: string,
): Promise<void> =>
  request<void>(
    `/buildings/${buildingId}/equipment-dependencies?source=${sourceId}&target=${targetId}`,
    { method: "DELETE" },
  );

// ── Analytics rules ───────────────────────────────────────────────────────────

export type FindingSeverity = "info" | "warning" | "critical";
export type FindingStatus = "open" | "acknowledged" | "resolved";
export type FindingCategory =
  "fault" | "comfort" | "data_quality" | "opportunity" | "configuration";

export interface Finding {
  id: string;
  rule_id: string;
  source_type: "equipment" | "zone" | "building";
  source_id: string;
  // Equipment tag / zone name for the source entity — null when there is no single answer
  // (a building-scoped conflict finding) or the entity was deleted since. Server-stamped
  // (docs/spec/terminology.md#finding-havainto); never client-joined against listEquipment /
  // listZones.
  source_label: string | null;
  building_id: string;
  finding_type: string;
  // Set only for cross-equipment conflict findings — the participant pair that triggered this
  // one, so two pairs conflicting in the same building have independent lifecycles. Null for
  // equipment- and zone-scoped findings.
  participant_key: string | null;
  category: FindingCategory;
  severity: FindingSeverity;
  evidence: Record<string, unknown>;
  data_point_names: string[];
  observation: string;
  explanation: string | null;
  remedy: string | null;
  status: FindingStatus;
  opened_at: string;
  resolved_at: string | null;
  acknowledged_by: string | null;
  created_at: string;
  updated_at: string;
}

export const listEquipmentFindings = (
  buildingId: string,
  equipmentId: string,
): Promise<Finding[]> =>
  request<Finding[]>(
    `/buildings/${buildingId}/equipment/${equipmentId}/findings`,
  );

export const listBuildingFindings = (
  buildingId: string,
  params: {
    status?: FindingStatus;
    sourceId?: string;
    dataPointName?: string;
  } = {},
): Promise<Finding[]> => {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.sourceId) q.set("source_id", params.sourceId);
  if (params.dataPointName) q.set("data_point_name", params.dataPointName);
  const qs = q.toString();
  return request<Finding[]>(
    `/buildings/${buildingId}/findings${qs ? `?${qs}` : ""}`,
  );
};

export const listFindings = listBuildingFindings;

// URL-scoped collection query (Havainnot, story #1248) — one request whose query string is the
// browser URL's own scope (building/group/city/type/status), resolved server-side, plus an
// optional perspective (category/source_type) for the judgment lenses (docs/spec/concepts.md
// §2.2/§2.4). Never a caller-resolved building_ids list — that fan-out shape was dropped in #1250.
export const listFindingsForScope = (
  scope: InventorySearch,
  perspective: {
    category?: FindingCategory;
    sourceType?: Finding["source_type"];
  } = {},
): Promise<Finding[]> => {
  const q = new URLSearchParams();
  if (scope.building) q.set("building", scope.building);
  if (scope.group) q.set("group", scope.group);
  if (scope.city) q.set("city", scope.city);
  if (scope.type) q.set("type", scope.type);
  if (scope.status) q.set("building_status", scope.status);
  if (perspective.category) q.set("category", perspective.category);
  if (perspective.sourceType) q.set("source_type", perspective.sourceType);
  const qs = q.toString();
  return request<Finding[]>(`/findings${qs ? `?${qs}` : ""}`);
};

export const listZoneFindings = (
  buildingId: string,
  zoneId: string,
): Promise<Finding[]> =>
  request<Finding[]>(`/buildings/${buildingId}/zones/${zoneId}/findings`);

export const acknowledgeFinding = (
  buildingId: string,
  findingId: string,
): Promise<Finding> =>
  request<Finding>(
    `/buildings/${buildingId}/findings/${findingId}/acknowledge`,
    { method: "POST", body: JSON.stringify({}) },
  );

export const resolveFinding = (
  buildingId: string,
  findingId: string,
): Promise<Finding> =>
  request<Finding>(`/buildings/${buildingId}/findings/${findingId}/resolve`, {
    method: "POST",
  });

// ── Equipment data point mappings ─────────────────────────────────────────────

// ── Equipment CSV upload ───────────────────────────────────────────────────────

export interface ColumnMatchOut {
  csv_column: string;
  canonical_id: string;
  name_fi: string;
  confidence: number;
  component_id: string;
}

export interface UploadPreviewOut {
  matched: ColumnMatchOut[];
  unmatched: string[];
  timestamp_column: string | null;
  row_count: number;
}

export interface UploadConfirmOut {
  rows_written: number;
  connector_id: string | null;
  /** Canonical ids that landed — for Signaalit Muokkaa ehdotus; confirm does not declare. */
  imported_data_points: string[];
}

export const uploadEquipmentDataPreview = (
  buildingId: string,
  equipmentId: string,
  file: File,
): Promise<UploadPreviewOut> =>
  uploadFile<UploadPreviewOut>(
    `/buildings/${buildingId}/equipment/${equipmentId}/data-points/upload/preview`,
    file,
  );

/** The multipart form field building-domain parses — one encoding, both targets. */
const confirmationField = (
  confirmedSchema: Record<string, string>,
  saveSchema: boolean,
) => ({
  confirmed_schema: JSON.stringify({
    confirmed_schema: confirmedSchema,
    save_schema: saveSchema,
  }),
});

export const confirmEquipmentDataUpload = (
  buildingId: string,
  equipmentId: string,
  file: File,
  confirmedSchema: Record<string, string>,
  saveSchema = true,
  dataContext: "baseline" | "operational" = "baseline",
): Promise<UploadConfirmOut> =>
  uploadFile<UploadConfirmOut>(
    `/buildings/${buildingId}/equipment/${equipmentId}/data-points/upload`,
    file,
    "",
    {
      ...confirmationField(confirmedSchema, saveSchema),
      data_context: dataContext,
    },
  );

// A zone is a peer import target of equipment: same request shape, same response
// shape, different path (docs/spec/concepts.md § source_id is polymorphic).
export const uploadZoneDataPreview = (
  buildingId: string,
  zoneId: string,
  file: File,
): Promise<UploadPreviewOut> =>
  uploadFile<UploadPreviewOut>(
    `/buildings/${buildingId}/zones/${zoneId}/data-points/upload/preview`,
    file,
  );

export const confirmZoneDataUpload = (
  buildingId: string,
  zoneId: string,
  file: File,
  confirmedSchema: Record<string, string>,
  saveSchema = true,
): Promise<UploadConfirmOut> =>
  uploadFile<UploadConfirmOut>(
    `/buildings/${buildingId}/zones/${zoneId}/data-points/upload`,
    file,
    "",
    confirmationField(confirmedSchema, saveSchema),
  );

// ── KB catalogue ──────────────────────────────────────────────────────────────

export interface KBDataPointDef {
  id: string;
  name_fi: string;
  unit: string;
  criticality: string;
  kind: string;
  order: number | null;
  connection_source: string | null;
}

export interface KBComponentDef {
  id: string;
  name_fi: string;
  data_points: KBDataPointDef[];
  optional: boolean;
  order: number | null;
}

export interface KBEquipmentDefinition {
  equipment_type: string;
  components: KBComponentDef[];
  /** Sensors shared across the type's components (AHU supply/outdoor/extract air
   *  temperature). They belong to no component, so any set of canonical
   *  data_point_ids must union these with the per-component ones. */
  shared_data_points: KBDataPointDef[];
}

export const getEquipmentKBDefinition = (
  buildingId: string,
  equipmentId: string,
): Promise<KBEquipmentDefinition> =>
  request<KBEquipmentDefinition>(
    `/buildings/${buildingId}/equipment/${equipmentId}/kb-definition`,
  );

/** Zones have no component grouping, so this is a flat list — the zone sibling
 *  of KBEquipmentDefinition. */
export interface KBZoneDefinition {
  zone_type: string;
  data_points: KBDataPointDef[];
}

export const getZoneKBDefinition = (
  buildingId: string,
  zoneId: string,
): Promise<KBZoneDefinition> =>
  request<KBZoneDefinition>(
    `/buildings/${buildingId}/zones/${zoneId}/kb-definition`,
  );

// Building's change stream (ADR-027 point 4; docs/reference/event-catalog.md). A browser's
// EventSource cannot send the Authorization header, so it opens the stream with a one-minute
// token Building issues for one building. The token only has to open the stream; a reconnect
// asks for a new one.
export interface BuildingEventStreamToken {
  token: string;
  expires_at: string; // ISO datetime
}

export const issueBuildingEventStreamToken = (
  buildingId: string,
): Promise<BuildingEventStreamToken> =>
  request<BuildingEventStreamToken>(`/buildings/${buildingId}/events/token`, {
    method: "POST",
  });

/** The stream URL for one building. `since` resumes after the last event id seen. */
export function buildingEventStreamUrl(
  buildingId: string,
  token: string,
  since: string | null,
): string {
  const params = new URLSearchParams({ stream_token: token });
  if (since !== null) params.set("since", since);
  return `${BASE}/buildings/${buildingId}/events?${params.toString()}`;
}
